import { useCallback, useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getEnvValue } from '../../../Helper/env';
import { useNavigation } from '@react-navigation/native';
import { useQueryClient } from '@tanstack/react-query';
import lodashFind from 'lodash/find';
import lodashIsEmpty from 'lodash/isEmpty';
import lodashPick from 'lodash/pick';

import modals from '../../../Components/Sheets/modals';
import { ModalManager } from '../../../Components/Web/Modal/hooks/useModalManager';
import { MODALPROMPT } from '../../../Components/Web/Modal/ModalPrompt/config';
import constants from '../../../Config/constants';
import StoreHelper from '../../../Helper/Store';
import useGoBack from '../../../Hooks/useGoBack';
import useMealPlan from '../../../Hooks/useMealPlan';
import routeList from '../../../Routes/list';
import { updateCheckoutDetails } from '../../../RTK/checkout';
import {
  checkoutOrderTypeSelector,
  checkoutStoreInfoSelector,
} from '../../../RTK/checkout/selectors';
import { checkout } from '../../../RTK/defaultValues';
import { whenFilterSelector } from '../../../RTK/filter/selectors';
import { setMealPlanType } from '../../../RTK/mealPlan';
import {
  shopOrderTypeKeysSelector,
  shopSelector,
} from '../../../RTK/shop/selectors';
import { selectPage } from '../../../RTK/utility/selectors';

const { ORDER_TYPES, ORDER_TYPE_DATA, ORDER_TYPE_API_KEYS, isWeb } = constants;

let isMealPlanTypeShowing = false;
const RAZZLE_BUILD_MODE = getEnvValue('RAZZLE_BUILD_MODE');
const isBranded = RAZZLE_BUILD_MODE === 'branded';
function useController({ isModal = false } = {}) {
  const dispatch = useDispatch();
  const goBack = useGoBack();
  const { isMealPlan, isMealPlanOnly, mealPlanDayCount, mealPlanType } =
    useMealPlan();

  const navigation = !isModal && useNavigation();
  const queryClient = useQueryClient();

  const page = useSelector(selectPage);
  const shopData = useSelector(shopSelector, shallowEqual);
  const shopOrderTypeKeys = useSelector(
    shopOrderTypeKeysSelector,
    shallowEqual,
  );
  const checkoutStoreInfo = useSelector(
    checkoutStoreInfoSelector,
    shallowEqual,
  );
  const selectedOrderType = useSelector(
    (state) =>
      checkoutOrderTypeSelector(
        state,
        shopData?.id || checkoutStoreInfo?.store_id,
        true,
      ),
    shallowEqual,
  );
  const whenFilter = useSelector(whenFilterSelector);

  const query = queryClient.getQueryData([shopData?.id, 'total-order-rating']);
  const hasRating = !lodashIsEmpty(query?.data);

  const storeOrderTypeKeys = useMemo(
    () =>
      lodashPick(
        page.isStore ? shopOrderTypeKeys : checkoutStoreInfo?.order_types,
        ORDER_TYPE_API_KEYS,
      ),
    [checkoutStoreInfo?.order_types, page.isStore, shopOrderTypeKeys],
  );

  const storeOrderTypes = useMemo(
    () => StoreHelper.getStoreAvailableOrderTypes(storeOrderTypeKeys),
    [storeOrderTypeKeys],
  );

  const showStoreNotAcceptingAdvancedOrder = useMemo(() => {
    if (isMealPlan) return false;
    const storeStatus = StoreHelper.getStoreAvailability(
      shopData?.store_hours,
      shopData?.off_dates,
      'object',
      whenFilter?.value,
    );
    return !storeStatus?.isOpen && !shopData?.is_accepting_in_advanced_orders;
  }, [isMealPlan, shopData, whenFilter?.value]);

  const _getMealPlanModalMethod = useCallback(() => {
    const modalName = isWeb
      ? MODALPROMPT.mealPlanTypeSelector
      : modals.MEAL_PLAN_TYPE_SELECTOR;
    const _show = constants.isWeb ? ModalManager.show : modals.show;
    const _getValue = (data) => (constants.isWeb ? data?.value : data);
    return {
      modalName,
      getValue: _getValue,
      show: _show,
    };
  }, []);

  // when user click the order type
  const _updateShopOrderType = useCallback(
    async (type) => {
      dispatch(
        updateCheckoutDetails({
          store_id: shopData?.id || checkoutStoreInfo?.store_id,
          keyToUpdate: checkout.keys.ORDER_TYPE,
          keyValue: type,
        }),
      );
    },
    [checkoutStoreInfo?.store_id, shopData?.id, dispatch],
  );

  // meal plan type selector
  const _showMealPlanSelector = useCallback(async () => {
    const { modalName, getValue, show } = _getMealPlanModalMethod();
    if (isMealPlanTypeShowing) return; // if meal plan type selector is already open, just return dont open it again
    isMealPlanTypeShowing = true;
    const result = await show(modalName, { value: mealPlanType }); // open meal plan type selector
    isMealPlanTypeShowing = false;
    const value = getValue(result);
    if (!lodashIsEmpty(value)) {
      // if user select meal plan type on the modal / action sheet
      if (!isMealPlan || isMealPlanOnly) {
        // just in case order type is not meal plan but the store only has meal plan type available
        // this will update the order type to meal plan if not yet set
        dispatch(
          updateCheckoutDetails({
            store_id: shopData?.id,
            keyToUpdate: checkout.keys.ORDER_TYPE,
            keyValue: lodashFind(ORDER_TYPE_DATA, {
              value: ORDER_TYPES.MEAL_PLAN,
            }),
          }),
        );
      }
      // update meal plan type to selected
      dispatch(setMealPlanType(value));
    } else if (isMealPlanOnly) {
      // if user did not select and just close the modal and the store only has meal plan type available
      goBack();
    } else {
      _updateShopOrderType(storeOrderTypes[0]);
    }
  }, [
    isMealPlan,
    isMealPlanOnly,
    mealPlanType,
    storeOrderTypes,
    shopData?.id,
    _getMealPlanModalMethod,
    _updateShopOrderType,
    dispatch,
    goBack,
  ]);

  // effect responsible for setting default order type on store page
  useEffect(() => {
    if (
      shopData?.id && // store page is loaded (if has shopData.id it means store page is loaded)
      page.isStore // user is on store page
    ) {
      // if store page is loaded AND user is on store page
      const hasNoOrderTypeOrInvalid = lodashIsEmpty(
        lodashFind(storeOrderTypes, { value: selectedOrderType?.value }), // return undefined if checkoutOrderType?.value is empty or selected order type does not exist on orderTypes
      );
      let paramOrderType;
      if (isBranded) {
        // user is accessing on branded mode
        const params = new URLSearchParams(document?.location?.search);
        paramOrderType = params.get('order_type');
      }
      if (hasNoOrderTypeOrInvalid) {
        _updateShopOrderType(
          paramOrderType
            ? lodashFind(ORDER_TYPE_DATA, { value: paramOrderType })
            : storeOrderTypes[0],
        );
      }
    }
  }, [
    page.isStore,
    selectedOrderType,
    shopData?.id,
    storeOrderTypes,
    _updateShopOrderType,
  ]);

  // effect responsible for setting meal plan type base on url parameter (branded only)
  useEffect(() => {
    if (
      shopData?.id && // store page is loaded (if has shopData.id it means store page is loaded)
      isBranded // user is accessing on branded mode
    ) {
      const params = new URLSearchParams(document?.location?.search);
      const paramMealPlanType = params.get('meal_plan_type');
      if (
        selectedOrderType?.value === constants.ORDER_TYPES.MEAL_PLAN && // selected order type is meal plan
        !mealPlanType && // if user is on meal plan order type and no meal plan type selected yet
        paramMealPlanType // has meal plan type on url parameter
      ) {
        dispatch(setMealPlanType(paramMealPlanType));
      }
    }
  }, [mealPlanType, selectedOrderType, shopData?.id, dispatch]);

  // effect responsible for showing meal plan type selector
  useEffect(() => {
    const selectedMealPlanType = isBranded
      ? new URLSearchParams(document?.location?.search).get('meal_plan_type') ||
        mealPlanType
      : mealPlanType;
    if (
      shopData?.id && // store page is loaded (if has shopData.id it means store page is loaded)
      isMealPlan && // meal plan selected order type
      (!isBranded || !isMealPlanOnly) && // always true on marketplace, must not be meal plan only store on branded env
      !selectedMealPlanType && // no meal plan type selected yet
      !isMealPlanTypeShowing // modal is not yet showing
    ) {
      _showMealPlanSelector();
    }
  }, [
    shopData?.id,
    isMealPlan,
    isMealPlanOnly,
    mealPlanType,
    _showMealPlanSelector,
  ]);

  // for showing store status
  const _getStoreStatus = (isForAccordion) => {
    if (lodashIsEmpty(shopData)) {
      return {};
    }
    return StoreHelper.getStoreHourStatus({
      storeHours: shopData?.store_hours,
      offDates: shopData?.off_dates,
      prepTime: shopData?.pre_order_to_order_queue_timer,
      isForAccordion,
      dateAndTime: whenFilter?.value, // to adjust on the date filter not just current date and time
      isPreOrderDate: !lodashIsEmpty(whenFilter?.value),
    });
  };
  // when user click the alert button to go to store additional info (timeslots, address etc)
  const _onAlertPressed = () => {
    navigation.navigate(routeList.STORE_INFORMATION);
  };

  return {
    hasRating,
    mealPlanDayCount,
    selectedOrderType,
    shopData,
    showStoreNotAcceptingAdvancedOrder,
    storeOrderTypes,
    getStoreStatus: _getStoreStatus,
    onAlertPressed: _onAlertPressed,
    updateShopOrderType: _updateShopOrderType,
  };
}

export default useController;
